import {
  Button,
  Checkbox,
  FormGroup,
  Icon,
  Input,
  Label,
  Margin,
  Padding,
  Paragraph,
  Shade,
  Size,
  Spinner,
  Text,
  TextLink,
  Title,
} from '@drawbotics/react-drylus';
import { useForm } from '@drawbotics/use-form';
import { Link, useAppContext, useController } from '@tokamakjs/react';
import { Fragment, useEffect, useState } from 'react';
import { z } from 'zod';

import { AuthFlow, Form, GoogleSSO, MicrosoftSSO, OptionSeparator } from '~/components';
import { PasswordValidation } from '~/components';
import { AppContext } from '~/types';
import { createTranslate, passwordSchema, translate as t } from '~/utils';

import { TargetDomain } from '../../types';
import { SignupController } from './signup.controller';

const tt = createTranslate('routes.sign_up');

export const createSignupSchema = () =>
  z
    .object({
      firstName: z.string().min(1, t('errors.field_empty')),
      lastName: z.string().min(1, t('errors.field_empty')),
      email: z.string().min(1, t('errors.field_empty')).email(t('errors.invalid_email')),
      password: passwordSchema,
      confirmPassword: z.string(),
      termsAccepted: z.boolean().refine((v) => v, {
        message: tt('accept_terms_error'),
      }),
    })
    .refine((val) => val.password === val.confirmPassword, {
      message: tt('passwords_do_not_match'),
      path: ['confirmPassword'],
    });

const SignupContent = ({ ctrl }: { ctrl: SignupController }) => {
  const appContext = useAppContext<AppContext>();

  const signupForm = useForm(createSignupSchema(), {
    defaultValues: {
      firstName: ctrl.invitation?.firstName ?? '',
      lastName: ctrl.invitation?.lastName ?? '',
      email: ctrl.invitation?.email ?? '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    },
  });

  const [showPassword, setShowpassword] = useState<boolean>(false);
  const [showConfirmationPassword, setShowConfirmationPassword] = useState<boolean>(false);
  /*
    WORKAROUND
    validate password and confirmPassword when writing, not just when unfocusing the input element

    this needs to be done asynchronously in useEffect and after the confirmPassword or password value changes,
    so that we use a new instance of validate() with the new form values in its closure
  */
  useEffect(() => {
    if (signupForm.values.confirmPassword) {
      signupForm.validate('confirmPassword');
    }
  }, [signupForm.values.confirmPassword]);

  useEffect(() => {
    if (signupForm.values.password) {
      signupForm.validate('password');
    }
  }, [signupForm.values.password]);

  return (
    <Fragment>
      <Title>{tt('sign_up')}</Title>
      <Paragraph>
        <Text shade={Shade.LIGHT}>{tt('have_account')}</Text>
        &nbsp;
        <Link noWarn href={location.search !== '' ? `/login${location.search}` : '/login'}>
          <TextLink>{tt('log_in')}</TextLink>
        </Link>
      </Paragraph>
      <Padding size={{ vertical: Size.SMALL }}>
        <GoogleSSO
          isInvitation={!!ctrl.invitation}
          isLoading={ctrl.isHandlingSuccessfulGoogleSignup}
          onSuccess={(r) => ctrl.handleSuccessfulGoogleSignup(r)}
          flow={AuthFlow.SIGN_UP}
        />
      </Padding>
      <Padding size={{ bottom: Size.DEFAULT }}>
        <MicrosoftSSO
          isInvitation={!!ctrl.invitation}
          isLoading={ctrl.isHandlingSuccessfulMicrosoftSignup}
          flow={AuthFlow.SIGN_UP}
          locationSearch={location.search}
          onSuccess={(r) => ctrl.handleSuccessfulMicrosoftSignup(r)}
        />
      </Padding>
      <Padding size={{ vertical: Size.SMALL }}>
        <OptionSeparator />
      </Padding>
      <Form onSubmit={() => {}}>
        <Margin size={{ top: Size.DEFAULT }}>
          <FormGroup
            label={<Label>{tt('first_name')}</Label>}
            input={
              <Input
                placeholder={tt('first_name_placeholder')}
                name="firstName"
                type="text"
                value={signupForm.get}
                onChange={signupForm.set}
                error={signupForm.getError}
                validate={signupForm.validate}
              />
            }
          />
        </Margin>

        <Margin size={{ top: Size.DEFAULT }}>
          <FormGroup
            label={<Label>{tt('last_name')}</Label>}
            input={
              <Input
                placeholder={tt('last_name_placeholder')}
                name="lastName"
                type="text"
                value={signupForm.get}
                onChange={signupForm.set}
                error={signupForm.getError}
                validate={signupForm.validate}
              />
            }
          />
        </Margin>

        <Margin size={{ top: Size.DEFAULT }}>
          <FormGroup
            label={<Label>{tt('email_address')}</Label>}
            input={
              <Input
                disabled={!!ctrl.invitation}
                placeholder={tt('email_placeholder')}
                name="email"
                type="email"
                value={signupForm.get}
                onChange={signupForm.set}
                error={signupForm.getError}
                validate={signupForm.validate}
              />
            }
          />
        </Margin>

        <Margin size={{ top: Size.DEFAULT }}>
          <FormGroup
            label={<Label>{tt('password')}</Label>}
            input={
              <Input
                placeholder={tt('password_placeholder')}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={signupForm.get}
                onChange={signupForm.set}
                validate={signupForm.validate}
                error={!!signupForm.getError('password')}
                trailing={
                  <Button
                    tabIndex="-1"
                    leading={<Icon name="eye" />}
                    onClick={() => setShowpassword(!showPassword)}
                  />
                }
              />
            }
          />
        </Margin>

        <Margin size={{ top: Size.SMALL }}>
          <PasswordValidation error={signupForm.errors.password} />
        </Margin>

        <Margin size={{ top: Size.DEFAULT }}>
          <FormGroup
            label={<Label>{tt('confirm_password')}</Label>}
            input={
              <Input
                placeholder={tt('confirm_password_placeholder')}
                name="confirmPassword"
                type={showConfirmationPassword ? 'text' : 'password'}
                value={signupForm.get}
                onChange={signupForm.set}
                validate={signupForm.validate}
                error={() => {
                  /*
                    zod 'refine' method only runs after all fields match the object schema

                    this means users would only get a '"Passwords don't match" error if they
                    have everything filled in the form including the terms acceptance checkbox

                    To show the message in any context of the form,
                    we will check that the passwords match in case zod hasn't done it already
                  */
                  const zodError = signupForm.getError('confirmPassword');

                  if (zodError) {
                    return zodError;
                  }

                  return signupForm.get('password') !== signupForm.get('confirmPassword')
                    ? tt('passwords_do_not_match')
                    : undefined;
                }}
                trailing={
                  <Button
                    tabIndex="-1"
                    leading={<Icon name="eye" />}
                    onClick={() => setShowConfirmationPassword(!showConfirmationPassword)}
                  />
                }
              />
            }
          />
        </Margin>

        <Margin size={{ top: Size.DEFAULT }}>
          <Checkbox
            name="termsAccepted"
            size={Size.LARGE}
            onChange={signupForm.set}
            error={signupForm.getError}
            validate={signupForm.validate}
            value={signupForm.get}>
            {tt('i_accept_the')}{' '}
            <a
              href={
                appContext.targetDomain === TargetDomain.DRAWBOTICS
                  ? 'https://drawbotics.com/terms-of-service'
                  : 'https://www.trymosaic.com/terms-of-use'
              }
              target="_blank"
              rel="noopener noreferrer">
              <TextLink>{tt('terms_of_service')}</TextLink>
            </a>
          </Checkbox>
        </Margin>

        <Margin size={{ top: Size.EXTRA_LARGE }}>
          <Padding size={{ bottom: Size.DEFAULT }}>
            <Button
              color={appContext.color}
              onClick={(e) => {
                e.preventDefault();

                const validationResult = signupForm.validateAll();

                if (validationResult.data) {
                  ctrl.signup(validationResult.data);
                }
              }}
              trailing={ctrl.isSubmitting ? <Spinner size={Size.SMALL} inversed /> : null}
              disabled={ctrl.isSubmitting}
              size={Size.LARGE}
              fullWidth>
              {tt('submit')}
            </Button>
          </Padding>
        </Margin>
      </Form>
    </Fragment>
  );
};

export const SignupView = () => {
  const ctrl = useController<SignupController>();

  return ctrl.invitationIsLoading ? (
    <Spinner fullSize size={Size.LARGE} />
  ) : (
    <SignupContent ctrl={ctrl} />
  );
};
